import { localDateKey } from '../lib/localDate';
import LoyaltyScanner from '../components/LoyaltyScanner';
import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Calendar, Check, X, Clock, MessageSquare, Trash2, Users, Download, Menu, Plus, BarChart3, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import apiService from '../services/apiService';
import LoadingSpinner from '../components/LoadingSpinner';
import AdminSidebar from '../components/AdminSidebar';
import ScheduleStoryDialog from '../components/ScheduleStoryDialog';

const AdminDashboard = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [slots, setSlots] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSlotDialog, setShowSlotDialog] = useState(false);
  const [creatingSlot, setCreatingSlot] = useState(false);
  const [newSlot, setNewSlot] = useState({ date: '', time: '' });
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // États pour les statistiques
  const [statistics, setStatistics] = useState(null);
  
  // États pour les filtres de rendez-vous
  const [appointmentFilter, setAppointmentFilter] = useState('all');
  
  // États pour la vue planning (mois/semaine)
  const [planningView, setPlanningView] = useState('month'); // 'month' ou 'week'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showStory, setShowStory] = useState(false);
  
  // États pour la fidélité
  const [loyaltyStats, setLoyaltyStats] = useState(null);
  const [qrCode, setQrCode] = useState('');
  const [validatingStamp, setValidatingStamp] = useState(false);
  const [stampResult, setStampResult] = useState(null);

  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'admin') {
      navigate('/connexion');
      return;
    }
    fetchData();
    fetchLoyaltyStats();
    fetchStatistics();
  }, [isAuthenticated, user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [appointmentsData, slotsData, reviewsData] = await Promise.all([
        apiService.getAppointments(),
        apiService.getSlots(),
        apiService.getAllReviews()
      ]);
      
      setAppointments(appointmentsData || []);
      setSlots(slotsData || []);
      setReviews(reviewsData || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les données",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const stats = await apiService.getAdminStatistics();
      setStatistics(stats);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const fetchLoyaltyStats = async () => {
    try {
      const stats = await apiService.getLoyaltyStats();
      setLoyaltyStats(stats);
    } catch (error) {
      console.error('Error fetching loyalty stats:', error);
    }
  };

  const validateStamp = async (e) => {
    e.preventDefault();
    if (validatingStamp) return;
    if (!/^\d{4}$/.test(qrCode.trim())) {
      toast({
        title: "Erreur",
        description: "Veuillez saisir un code QR",
        variant: "destructive"
      });
      return;
    }
    
    setValidatingStamp(true);
    try {
      const result = await apiService.validateStamp(qrCode.trim());
      setStampResult(result);
      
      if (result.success) {
        toast({
          title: result.discount_unlocked ? "Réduction débloquée !" : "Tampon validé",
          description: result.message,
          variant: "default"
        });
        setQrCode('');
        fetchLoyaltyStats();
      } else {
        toast({
          title: "Validation échouée",
          description: result.message,
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error validating stamp:', error);
      toast({
        title: "Erreur",
        description: "Impossible de valider le tampon",
        variant: "destructive"
      });
    } finally {
      setValidatingStamp(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Date non spécifiée';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'Date non spécifiée';
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'Heure non spécifiée';
    return timeString;
  };

  const createSlot = async (e) => {
    e.preventDefault();
    
    if (!newSlot.date || !newSlot.time) {
      toast({
        title: "Erreur",
        description: "Veuillez remplir tous les champs",
        variant: "destructive"
      });
      return;
    }

    if (creatingSlot) return;
    if (new Date(`${newSlot.date}T${newSlot.time}`) <= new Date()) {
      toast({ title: "Date passée", description: "Choisissez une date et une heure à venir.", variant: "destructive" });
      return;
    }
    if (slots.some(slot => slot.date === newSlot.date && slot.start_time?.slice(0, 5) === newSlot.time)) {
      toast({ title: "Créneau déjà présent", description: "Cette date et cette heure figurent déjà dans le planning.", variant: "destructive" });
      return;
    }
    setCreatingSlot(true);
    try {
      await apiService.createSlot({
        date: newSlot.date,
        time: newSlot.time
      });
      
      toast({
        title: "Succès",
        description: "Créneau créé avec succès",
      });
      
      setNewSlot({ date: '', time: '' });
      setShowSlotDialog(false);
      fetchData();
      fetchStatistics();
    } catch (error) {
      console.error('Error creating slot:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible de créer le créneau",
        variant: "destructive"
      });
    } finally { setCreatingSlot(false); }
  };

  const updateAppointmentStatus = async (appointmentId, status) => {
    try {
      await apiService.updateAppointmentStatus(appointmentId, status);
      setAppointments(appointments.map(app => 
        app.id === appointmentId ? { ...app, status } : app
      ));
      toast({
        title: "Succès",
        description: `Rendez-vous ${status === 'confirmed' ? 'confirmé' : 'mis à jour'} avec succès`,
      });
      fetchStatistics();
    } catch (error) {
      console.error('Error updating appointment:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible de mettre à jour le rendez-vous",
        variant: "destructive"
      });
    }
  };

  const deleteAppointment = async (appointmentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer définitivement ce rendez-vous ?')) {
      return;
    }
    
    try {
      await apiService.deleteAppointment(appointmentId);
      setAppointments(appointments.filter(app => app.id !== appointmentId));
      toast({
        title: "Succès",
        description: "Rendez-vous supprimé avec succès",
      });
      fetchStatistics();
    } catch (error) {
      console.error('Error deleting appointment:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible de supprimer le rendez-vous",
        variant: "destructive"
      });
    }
  };

  const cancelAppointment = async (appointmentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler ce rendez-vous ? Le client sera automatiquement notifié par email.')) {
      return;
    }
    
    try {
      await apiService.cancelAppointment(appointmentId);
      setAppointments(appointments.map(app => 
        app.id === appointmentId ? { ...app, status: 'cancelled' } : app
      ));
      toast({
        title: "Succès",
        description: "Rendez-vous annulé avec succès et client notifié par email",
      });
      fetchStatistics();
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible d'annuler le rendez-vous",
        variant: "destructive"
      });
    }
  };

  const isAppointmentOlderThan24Hours = (appointment) => {
    if (!appointment.slot_info || !appointment.slot_info.date || !appointment.slot_info.start_time) {
      return false;
    }
    
    try {
      const appointmentDate = new Date(appointment.slot_info.date);
      const [hours, minutes] = appointment.slot_info.start_time.split(':');
      appointmentDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      
      const now = new Date();
      const diffInHours = (now - appointmentDate) / (1000 * 60 * 60);
      
      return diffInHours > 24;
    } catch (error) {
      console.error('Error calculating appointment age:', error);
      return false;
    }
  };

  const updateReviewStatus = async (reviewId, status) => {
    try {
      await apiService.updateReviewStatus(reviewId, status);
      
      toast({
        title: "Succès",
        description: `Avis ${status === 'approved' ? 'approuvé' : 'rejeté'} !`
      });
      
      fetchData();
      fetchStatistics();
    } catch (error) {
      console.error('Error updating review:', error);
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour l'avis",
        variant: "destructive"
      });
    }
  };

  const deleteSlot = async (slotId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce créneau ?')) {
      return;
    }
    
    try {
      await apiService.deleteSlot(slotId);
      setSlots(slots.filter(slot => slot.id !== slotId));
      toast({
        title: "Succès",
        description: "Créneau supprimé avec succès",
      });
    } catch (error) {
      console.error('Error deleting slot:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible de supprimer le créneau",
        variant: "destructive"
      });
    }
  };

  const deleteOldSlots = async () => {
    // Filtrer les créneaux passés de plus de 24h
    const now = new Date();
    const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    
    const oldSlots = slots.filter(slot => {
      try {
        const slotDate = new Date(slot.date);
        const [hours, minutes] = slot.start_time.split(':');
        slotDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        return slotDate < twentyFourHoursAgo;
      } catch (error) {
        return false;
      }
    });

    if (oldSlots.length === 0) {
      toast({
        title: "Information",
        description: "Aucun créneau passé de plus de 24h à supprimer",
      });
      return;
    }

    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${oldSlots.length} créneau(x) passé(s) de plus de 24 heures ?`)) {
      return;
    }

    try {
      let successCount = 0;
      let errorCount = 0;

      for (const slot of oldSlots) {
        try {
          await apiService.deleteSlot(slot.id);
          successCount++;
        } catch (error) {
          console.error(`Error deleting slot ${slot.id}:`, error);
          errorCount++;
        }
      }

      // Rafraîchir les données
      await fetchData();
      await fetchStatistics();

      if (errorCount === 0) {
        toast({
          title: "Succès",
          description: `${successCount} créneau(x) supprimé(s) avec succès`,
        });
      } else {
        toast({
          title: "Partiellement réussi",
          description: `${successCount} créneau(x) supprimé(s), ${errorCount} erreur(s)`,
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error deleting old slots:', error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer les créneaux passés",
        variant: "destructive"
      });
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { label: 'En attente', variant: 'secondary' },
      'confirmed': { label: 'Confirmé', variant: 'default' },
      'completed': { label: 'Terminé', variant: 'success' },
      'cancelled': { label: 'Annulé', variant: 'destructive' }
    };

    const config = statusConfig[status] || { label: status, variant: 'secondary' };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const parseLieuFromNotes = (notes) => {
    if (!notes) return null;
    // Chercher le format exact enregistré : "📍 Lieu: Chez moi" ou "Chez vous" ou "Autre"
    const match = notes.match(/📍\s*Lieu\s*:\s*([^\n\r]+)/i);
    if (match) {
      const lieu = match[1].trim();
      // Retourner le lieu tel quel
      return lieu;
    }
    return null;
  };

  const parseInstagramFromNotes = (notes) => {
    if (!notes) return null;
    const match = notes.match(/📱\s*Instagram\s*:\s*([^\n\r]+)/i);
    return match ? match[1].trim() : null;
  };

  const parsePersonnesFromNotes = (notes) => {
    if (!notes) return null;
    // Chercher "👥 Personnes:" ou "👥 Nombre de personnes:"
    const match = notes.match(/👥\s*(?:Nombre de )?Personnes?\s*:\s*([^\n\r]+)/i);
    return match ? match[1].trim() : null;
  };

  const parseActualNotesFromNotes = (notes) => {
    if (!notes) return null;
    const infoMatch = notes.match(/ℹ️\s*Informations supplémentaires\s*:\s*\n(.+?)(?=\n\n|$)/s);
    if (infoMatch) {
      return infoMatch[1].trim();
    }
    let cleanNotes = notes
      .replace(/📍\s*Lieu\s*:\s*[^\n\r]+/gi, '')
      .replace(/👥\s*Nombre de personnes\s*:\s*[^\n\r]+/gi, '')
      .replace(/📱\s*Instagram\s*:\s*[^\n\r]+/gi, '')
      .replace(/ℹ️\s*Informations\s*supplémentaires\s*:\s*[^\n\r]+/gi, '')
      .trim();
    cleanNotes = cleanNotes.replace(/\n\s*\n/g, '\n').trim();
    return cleanNotes || null;
  };

  // Filtrer les rendez-vous
  const filteredAppointments = useMemo(() => {
    if (appointmentFilter === 'all') return appointments;
    return appointments.filter(app => app.status === appointmentFilter);
  }, [appointments, appointmentFilter]);

  // Fonction pour obtenir le premier et dernier jour du mois
  const getMonthBounds = (date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    return { firstDay, lastDay };
  };

  // Fonction pour obtenir le premier et dernier jour de la semaine
  const getWeekBounds = (date) => {
    const day = date.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    const monday = new Date(date);
    monday.setDate(date.getDate() + diff);
    monday.setHours(0, 0, 0, 0);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    return { monday, sunday };
  };

  // Fonction pour naviguer dans le calendrier
  const navigateCalendar = (direction) => {
    const newDate = new Date(currentDate);
    if (planningView === 'month') {
      newDate.setMonth(currentDate.getMonth() + direction);
    } else {
      newDate.setDate(currentDate.getDate() + (direction * 7));
    }
    setCurrentDate(newDate);
  };

  // Générer les jours du mois avec les créneaux
  const generateMonthCalendar = () => {
    const { firstDay, lastDay } = getMonthBounds(currentDate);
    const firstDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    const startPadding = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    
    const days = [];
    
    // Padding jours précédents
    for (let i = 0; i < startPadding; i++) {
      days.push({ date: null, slots: [] });
    }
    
    // Jours du mois
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const dateStr = localDateKey(date);
      const daySlots = slots.filter(slot => {
        const slotDateStr = slot.date.slice(0, 10);
        return slotDateStr === dateStr;
      });
      days.push({ date, slots: daySlots });
    }
    
    return days;
  };

  if (!isAuthenticated || user?.role !== 'admin') {
    return <div>Accès non autorisé</div>;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex items-center justify-center min-h-screen">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="korean-admin min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <AdminSidebar 
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        onLogout={handleLogout}
        isMobileOpen={isMobileMenuOpen}
        setIsMobileOpen={setIsMobileMenuOpen}
      />

      {/* Main Content */}
      <div className="flex-1 lg:ml-72">
        {/* Top Bar Mobile */}
        <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-30">
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            aria-label="Ouvrir le menu admin" className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Menu className="w-6 h-6 text-gray-700" />
          </button>
          <h1 className="text-lg font-semibold text-gray-900">Admin</h1>
          <div className="w-10" />
        </div>

        {/* Content Area */}
        <div className="p-4 lg:p-8">
          {/* Dashboard Tab - Statistiques */}
          {activeTab === 'dashboard' && (
            <div>
              <div className="mb-8">
                <h1 className="text-4xl font-bold text-gray-900 mb-2">Tableau de bord</h1>
                <p className="text-gray-600">Vue d'ensemble de votre activité</p><div className="korean-admin-shortcuts"><button className="korean-button" onClick={() => { setActiveTab('slots'); setShowSlotDialog(true); }}>Ajouter un créneau</button><button className="korean-button korean-button-outline" onClick={() => setActiveTab('loyalty')}>Scanner une carte de fidélité</button></div>
              </div>

              {!statistics ? (
                <div className="flex items-center justify-center py-12">
                  <LoadingSpinner />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Statistiques des rendez-vous */}
                  <div>
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">Rendez-vous</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0 shadow-lg hover:shadow-xl transition-shadow">
                        <CardContent className="p-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-blue-100 text-sm font-medium mb-1">Total</p>
                              <p className="text-4xl font-bold">{statistics.appointments.total}</p>
                            </div>
                            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                              <Calendar className="w-6 h-6" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white border-0 shadow-lg hover:shadow-xl transition-shadow">
                        <CardContent className="p-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-amber-100 text-sm font-medium mb-1">En attente</p>
                              <p className="text-4xl font-bold">{statistics.appointments.pending}</p>
                            </div>
                            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                              <Clock className="w-6 h-6" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0 shadow-lg hover:shadow-xl transition-shadow">
                        <CardContent className="p-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-green-100 text-sm font-medium mb-1">Confirmés</p>
                              <p className="text-4xl font-bold">{statistics.appointments.confirmed}</p>
                            </div>
                            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                              <Check className="w-6 h-6" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white border-0 shadow-lg hover:shadow-xl transition-shadow">
                        <CardContent className="p-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-red-100 text-sm font-medium mb-1">Annulés</p>
                              <p className="text-4xl font-bold">{statistics.appointments.cancelled}</p>
                            </div>
                            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                              <X className="w-6 h-6" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>

                  {/* Statistiques des avis */}
                  <div>
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">⭐ Avis clients</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0 shadow-lg hover:shadow-xl transition-shadow">
                        <CardContent className="p-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-purple-100 text-sm font-medium mb-1">Total avis</p>
                              <p className="text-4xl font-bold">{statistics.reviews.total}</p>
                            </div>
                            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                              <Star className="w-6 h-6" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white border-0 shadow-lg hover:shadow-xl transition-shadow">
                        <CardContent className="p-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-orange-100 text-sm font-medium mb-1">En attente</p>
                              <p className="text-4xl font-bold">{statistics.reviews.pending}</p>
                            </div>
                            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                              <Clock className="w-6 h-6" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="bg-gradient-to-br from-teal-500 to-teal-600 text-white border-0 shadow-lg hover:shadow-xl transition-shadow">
                        <CardContent className="p-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-teal-100 text-sm font-medium mb-1">Approuvés</p>
                              <p className="text-4xl font-bold">{statistics.reviews.approved}</p>
                            </div>
                            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                              <Check className="w-6 h-6" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>

                  {/* Actions rapides */}
                  <div>
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">⚡ Actions rapides</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setActiveTab('appointments')}>
                        <CardContent className="p-6">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                              <Calendar className="w-6 h-6 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">Gérer les rendez-vous</p>
                              <p className="text-sm text-gray-500">Confirmer ou annuler</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setActiveTab('slots')}>
                        <CardContent className="p-6">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                              <Clock className="w-6 h-6 text-amber-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">Planning</p>
                              <p className="text-sm text-gray-500">Vue mois/semaine</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setActiveTab('reviews')}>
                        <CardContent className="p-6">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                              <Star className="w-6 h-6 text-purple-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">Modérer les avis</p>
                              <p className="text-sm text-gray-500">Approuver ou rejeter</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Appointments Tab */}
          {activeTab === 'appointments' && (
            <div>
              <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Rendez-vous</h1>
                <p className="text-gray-600">Gérez les réservations de vos clients ({filteredAppointments.length})</p>
              </div>

              {/* Filtres */}
              <div className="mb-6 flex flex-wrap gap-2">
                <Button
                  variant={appointmentFilter === 'all' ? 'default' : 'outline'}
                  onClick={() => setAppointmentFilter('all')}
                  className={appointmentFilter === 'all' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                  data-testid="filter-all-btn"
                >
                  Tous ({appointments.length})
                </Button>
                <Button
                  variant={appointmentFilter === 'pending' ? 'default' : 'outline'}
                  onClick={() => setAppointmentFilter('pending')}
                  className={appointmentFilter === 'pending' ? 'bg-amber-600 hover:bg-amber-700' : ''}
                  data-testid="filter-pending-btn"
                >
                  En attente ({appointments.filter(a => a.status === 'pending').length})
                </Button>
                <Button
                  variant={appointmentFilter === 'confirmed' ? 'default' : 'outline'}
                  onClick={() => setAppointmentFilter('confirmed')}
                  className={appointmentFilter === 'confirmed' ? 'bg-green-600 hover:bg-green-700' : ''}
                  data-testid="filter-confirmed-btn"
                >
                  Confirmés ({appointments.filter(a => a.status === 'confirmed').length})
                </Button>
                <Button
                  variant={appointmentFilter === 'cancelled' ? 'default' : 'outline'}
                  onClick={() => setAppointmentFilter('cancelled')}
                  className={appointmentFilter === 'cancelled' ? 'bg-red-600 hover:bg-red-700' : ''}
                  data-testid="filter-cancelled-btn"
                >
                  Annulés ({appointments.filter(a => a.status === 'cancelled').length})
                </Button>
              </div>

              <div className="space-y-4">
                {filteredAppointments.map((appointment) => {
                  const lieu = parseLieuFromNotes(appointment.notes);
                  const instagram = parseInstagramFromNotes(appointment.notes);
                  const phone = appointment.notes?.match(/(?:^|\n)Téléphone:\s*([^\n]+)/)?.[1];
                  const personnes = parsePersonnesFromNotes(appointment.notes);
                  const actualNotes = parseActualNotesFromNotes(appointment.notes);
                  const canDelete = isAppointmentOlderThan24Hours(appointment);
                  
                  return (
                    <Card key={appointment.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                      <CardContent className="p-6">
                        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                          <div className="flex-1 space-y-4">
                            {/* Header */}
                            <div className="flex items-start justify-between">
                              <div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">
                                  {appointment.service_name || 'Service non spécifié'}
                                </h3>
                                <div className="flex flex-wrap gap-2">
                                  <Badge variant="outline" className="gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {appointment.slot_info ? formatDate(appointment.slot_info.date) : 'Date non spécifiée'}
                                  </Badge>
                                  <Badge variant="outline" className="gap-1">
                                    <Clock className="w-3 h-3" />
                                    {appointment.slot_info ? formatTime(appointment.slot_info.start_time) : 'Heure non spécifiée'}
                                  </Badge>
                                </div>
                              </div>
                              <div className="flex flex-col items-end gap-2">
                                {getStatusBadge(appointment.status)}
                                <div className="text-xl font-bold text-amber-600">
                                  {appointment.service_price || 0}€
                                </div>
                              </div>
                            </div>

                            {/* Client Info */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <Users className="w-5 h-5 text-blue-600" />
                                <div>
                                  <div className="text-xs text-blue-700 font-semibold uppercase tracking-wide">Client</div>
                                  <div className="font-bold text-gray-900">{appointment.user_name || 'Utilisateur inconnu'}</div>
                                  <div className="text-sm text-gray-700">{appointment.user_email || 'Email non disponible'}</div>{phone && <div className="text-sm text-gray-900 mt-1"><strong>Téléphone :</strong> {phone}</div>}
                                </div>
                              </div>

                              {lieu && <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                                <span className="text-2xl">📍</span>
                                <div>
                                  <div className="text-xs text-green-700 font-semibold uppercase tracking-wide">Lieu de prestation</div>
                                  <div className="font-bold text-gray-900 text-base">
                                    {lieu || 'Non spécifié'}
                                  </div>
                                </div>
                              </div>}

                            </div>

                            {/* Additional Info */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {personnes && <div className="flex items-center gap-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                                <span className="text-2xl">👥</span>
                                <div>
                                  <div className="text-xs text-purple-700 font-semibold uppercase tracking-wide">Nombre de personnes</div>
                                  <div className="font-bold text-gray-900 text-base">{personnes || 'Non spécifié'}</div>
                                </div>
                              </div>}

                              {instagram && (
                                <div className="flex items-center gap-3 p-3 bg-pink-50 border border-pink-200 rounded-lg">
                                  <span className="text-2xl">📱</span>
                                  <div>
                                    <div className="text-xs text-pink-700 font-semibold uppercase tracking-wide">Instagram</div>
                                    <div className="font-bold text-gray-900 text-base">{instagram}</div>
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Notes */}
                            {actualNotes && (
                              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                                <div className="flex items-start gap-2">
                                  <MessageSquare className="w-4 h-4 text-blue-600 mt-0.5" />
                                  <div>
                                    <div className="text-xs text-blue-600 font-medium mb-1">Notes du client</div>
                                    <div className="text-sm text-gray-700 whitespace-pre-line">{actualNotes}</div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Actions */}
                            <div className="flex flex-wrap gap-2 pt-2">
                              {appointment.status === 'pending' && (
                                <Button 
                                  size="sm" 
                                  onClick={() => updateAppointmentStatus(appointment.id, 'confirmed')}
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                  data-testid="confirm-appointment-btn"
                                >
                                  <Check className="w-4 h-4 mr-1" />
                                  Confirmer
                                </Button>
                              )}
                              {appointment.status !== 'cancelled' && !canDelete && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => cancelAppointment(appointment.id)}
                                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                                  data-testid="cancel-appointment-btn"
                                >
                                  <X className="w-4 h-4 mr-1" />
                                  Annuler
                                </Button>
                              )}
                              {canDelete && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => deleteAppointment(appointment.id)}
                                  className="border-red-300 text-red-700 hover:bg-red-50"
                                  data-testid="delete-appointment-btn"
                                >
                                  <Trash2 className="w-4 h-4 mr-1" />
                                  Supprimer
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}

                {filteredAppointments.length === 0 && (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">Aucun rendez-vous pour ce filtre</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          )}

          {/* Slots Tab */}
          {activeTab === 'slots' && (
            <div>
              <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">Planning</h1>
                  <p className="text-gray-600">Gérez vos créneaux disponibles</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button 
                    onClick={deleteOldSlots}
                    variant="outline"
                    className="gap-2 text-red-600 hover:bg-red-50 hover:text-red-700 border-red-300"
                    data-testid="delete-old-slots-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span className="hidden sm:inline">Supprimer créneaux passés</span>
                  </Button>
                  <Button 
                    onClick={() => setShowStory(true)}
                    variant="outline"
                    className="gap-2"
                    data-testid="export-schedule-btn"
                  >
                    <Download className="w-4 h-4" />
                    <span>Story Instagram</span>
                  </Button>
                  <ScheduleStoryDialog open={showStory} onOpenChange={setShowStory} currentDate={currentDate} view={planningView} />
                  <Dialog open={showSlotDialog} onOpenChange={setShowSlotDialog}>
                    <DialogTrigger asChild>
                      <Button className="bg-amber-600 hover:bg-amber-700 gap-2" data-testid="add-slot-btn">
                        <Plus className="w-4 h-4" />
                        <span>Ajouter un créneau</span>
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="korean-admin-dialog">
                      <DialogHeader>
                        <DialogTitle>Ouvrir un créneau</DialogTitle><p className="text-sm text-neutral-600">Choisissez la date et l’heure de début. Le créneau dure une heure et devient disponible à la réservation.</p>
                      </DialogHeader>
                      <form onSubmit={createSlot} className="space-y-4">
                        <div>
                          <Label htmlFor="date">Date du rendez-vous</Label>
                          <Input
                            id="date"
                            min={localDateKey(new Date())}
                            type="date"
                            value={newSlot.date}
                            onChange={(e) => setNewSlot({...newSlot, date: e.target.value})}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="time">Heure</Label>
                          <Input
                            id="time"
                            type="time"
                            value={newSlot.time}
                            onChange={(e) => setNewSlot({...newSlot, time: e.target.value})}
                            required
                          />
                        </div>
                        <DialogFooter>
                          <Button type="button" variant="outline" onClick={() => setShowSlotDialog(false)}>
                            Annuler
                          </Button>
                          <Button type="submit" disabled={creatingSlot} className="bg-amber-600 hover:bg-amber-700">
                            {creatingSlot ? "Création…" : "Créer le créneau"}
                          </Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>

              <div className="korean-admin-guide"><strong>Ouvrir vos disponibilités</strong><p>1. Ajouter un créneau · 2. Choisir la date et l’heure · 3. Créer le créneau</p><p>Répétez pour chaque rendez-vous disponible. Les créneaux réservés apparaissent dans le planning.</p></div>
              {/* Vue toggle et navigation */}
              <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex gap-2">
                  <Button
                    variant={planningView === 'month' ? 'default' : 'outline'}
                    onClick={() => setPlanningView('month')}
                    className={planningView === 'month' ? 'bg-amber-600 hover:bg-amber-700' : ''}
                    data-testid="view-month-btn"
                  >
                    Vue Mois
                  </Button>
                  <Button
                    variant={planningView === 'week' ? 'default' : 'outline'}
                    onClick={() => setPlanningView('week')}
                    className={planningView === 'week' ? 'bg-amber-600 hover:bg-amber-700' : ''}
                    data-testid="view-week-btn"
                  >
                    Vue Semaine
                  </Button>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigateCalendar(-1)}
                    data-testid="prev-period-btn"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <div className="px-4 py-2 bg-white border border-gray-200 rounded-lg font-semibold text-gray-900 min-w-[200px] text-center">
                    {planningView === 'month' 
                      ? currentDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
                      : (() => {
                          const { monday, sunday } = getWeekBounds(currentDate);
                          return `${monday.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })} - ${sunday.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}`;
                        })()
                    }
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigateCalendar(1)}
                    data-testid="next-period-btn"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Vue Mois */}
              {planningView === 'month' && (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  {/* En-tête des jours */}
                  <div className="grid grid-cols-7 border-b border-gray-200">
                    {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((day) => (
                      <div key={day} className="p-3 text-center font-semibold text-gray-700 bg-gray-50">
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Grille du calendrier */}
                  <div className="grid grid-cols-7">
                    {generateMonthCalendar().map((day, index) => {
                      const isToday = day.date && 
                        day.date.toDateString() === new Date().toDateString();
                      const hasSlots = day.slots && day.slots.length > 0;
                      const availableSlots = day.slots.filter(s => s.is_available).length;
                      
                      return (
                        <div
                          key={index}
                          className={`min-h-[100px] p-2 border-b border-r border-gray-200 ${
                            !day.date ? 'bg-gray-50' : 'bg-white hover:bg-gray-50'
                          } ${isToday ? 'bg-amber-50' : ''}`}
                        >
                          {day.date && (
                            <>
                              <div className={`text-sm font-semibold mb-2 ${
                                isToday ? 'text-amber-600' : 'text-gray-700'
                              }`}>
                                {day.date.getDate()}
                              </div>
                              {hasSlots && (
                                <div className="space-y-1">
                                  {day.slots.slice(0, 3).map((slot) => (
                                    <div
                                      key={slot.id}
                                      className={`text-xs p-1 rounded ${
                                        slot.is_available 
                                          ? 'bg-green-100 text-green-700'
                                          : 'bg-red-100 text-red-700'
                                      }`}
                                    >
                                      {slot.start_time}
                                    </div>
                                  ))}
                                  {day.slots.length > 3 && (
                                    <div className="text-xs text-gray-500 font-medium">
                                      +{day.slots.length - 3} autre(s)
                                    </div>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Vue Semaine */}
              {planningView === 'week' && (
                <div className="space-y-4">
                  {(() => {
                    const { monday, sunday } = getWeekBounds(currentDate);
                    const weekDays = [];
                    for (let i = 0; i < 7; i++) {
                      const date = new Date(monday);
                      date.setDate(monday.getDate() + i);
                      weekDays.push(date);
                    }

                    return weekDays.map((date) => {
                      const dateStr = localDateKey(date);
                      const daySlots = slots.filter(slot => {
                        const slotDateStr = slot.date.slice(0, 10);
                        return slotDateStr === dateStr;
                      }).sort((a, b) => a.start_time.localeCompare(b.start_time));

                      const isToday = date.toDateString() === new Date().toDateString();

                      return (
                        <Card key={dateStr} className={`${
                          isToday ? 'border-amber-400 border-2' : ''
                        }`}>
                          <CardHeader className={`${
                            isToday ? 'bg-amber-50' : 'bg-gray-50'
                          } border-b border-gray-200`}>
                            <CardTitle className="text-lg flex items-center justify-between">
                              <span>{date.toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long' })}</span>
                              {daySlots.length > 0 && (
                                <Badge variant="outline">
                                  {daySlots.filter(s => s.is_available).length} disponible(s)
                                </Badge>
                              )}
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="p-4">
                            {daySlots.length === 0 ? (
                              <p className="text-gray-500 text-center py-4">Aucun créneau</p>
                            ) : (
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {daySlots.map((slot) => (
                                  <div
                                    key={slot.id}
                                    className={`p-4 rounded-lg border-2 flex items-center justify-between ${
                                      slot.is_available
                                        ? 'bg-green-50 border-green-200'
                                        : 'bg-red-50 border-red-200'
                                    }`}
                                  >
                                    <div>
                                      <div className="font-semibold text-gray-900">
                                        {slot.start_time} - {slot.end_time}
                                      </div>
                                      <Badge
                                        variant={slot.is_available ? 'default' : 'secondary'}
                                        className="mt-1"
                                      >
                                        {slot.is_available ? 'Disponible' : 'Réservé'}
                                      </Badge>
                                    </div>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => deleteSlot(slot.id)}
                                      className="border-red-300 text-red-700 hover:bg-red-50"
                                      data-testid="delete-slot-btn"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          )}

          {/* Reviews Tab */}
          {activeTab === 'reviews' && (
            <div>
              <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Avis clients</h1>
                <p className="text-gray-600">Modérez les avis de vos clients ({reviews.length})</p>
              </div>

              <div className="space-y-4">
                {reviews.map((review) => (
                  <Card key={review.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <div className="font-semibold text-gray-900 mb-1">{review.user_name || 'Utilisateur inconnu'}</div>
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <span key={i} className={`text-lg ${i < review.rating ? 'text-yellow-400' : 'text-gray-300'}`}>
                                ⭐
                              </span>
                            ))}
                          </div>
                        </div>
                        <Badge variant={review.status === 'approved' ? 'default' : review.status === 'pending' ? 'secondary' : 'destructive'}>
                          {review.status === 'approved' ? 'Approuvé' : review.status === 'pending' ? 'En attente' : 'Rejeté'}
                        </Badge>
                      </div>
                      <p className="text-gray-700 mb-4">{review.comment}</p>
                      {review.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            onClick={() => updateReviewStatus(review.id, 'approved')}
                            className="bg-green-600 hover:bg-green-700 text-white"
                            data-testid="approve-review-btn"
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Approuver
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => updateReviewStatus(review.id, 'rejected')}
                            className="border-red-300 text-red-700 hover:bg-red-50"
                            data-testid="reject-review-btn"
                          >
                            <X className="w-4 h-4 mr-1" />
                            Rejeter
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}

                {reviews.length === 0 && (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">Aucun avis pour le moment</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          )}

          {/* Loyalty Tab */}
          {activeTab === 'loyalty' && (
            <div>
              <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Fidélité</h1>
                <p className="text-gray-600">Gérez le programme de fidélité</p>
              </div>

              <div className="space-y-6">
                {/* Validation Form */}
                <Card>
                  <CardHeader className="bg-gradient-to-r from-amber-500 to-amber-600 text-white">
                    <CardTitle className="flex items-center gap-2">
                      Valider un Tampon
                    </CardTitle>
                    <CardDescription className="text-amber-100">
                      Scannez une carte ou saisissez les 4 chiffres de la cliente
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-6">
                    <LoyaltyScanner disabled={validatingStamp} onDetected={code => { setQrCode(code); setStampResult(null); }} />
                    <form onSubmit={validateStamp} className="space-y-4">
                      <div>
                        <Label htmlFor="qrCode" className="text-lg font-semibold">Code QR Client</Label>
                        <Input
                          id="qrCode"
                          type="text"
                          value={qrCode}
                          inputMode="numeric" pattern="[0-9]{4}" onChange={(e) => { setQrCode(e.target.value.replace(/\D/g, "")); setStampResult(null); }}
                          placeholder="1234"
                          maxLength={4}
                          className="text-center text-2xl font-mono h-14"
                          required
                          data-testid="qr-code-input"
                        />
                        <p className="text-sm text-gray-500 mt-2">
                          Le client vous présente son QR code ou donne les 4 chiffres
                        </p>
                      </div>
                      
                      <Button 
                        type="submit"
                        disabled={validatingStamp || qrCode.length !== 4}
                        className="w-full bg-amber-600 hover:bg-amber-700 h-12 text-lg"
                        data-testid="validate-stamp-btn"
                      >
                        {validatingStamp ? 'Validation en cours...' : 'Valider le Tampon'}
                      </Button>
                    </form>

                    {stampResult && (
                      <div className={`mt-4 p-4 rounded-lg ${stampResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                        <div className={`font-bold mb-2 ${stampResult.success ? 'text-green-800' : 'text-red-800'}`}>
                          {stampResult.success ? 'Succès' : 'Échec'}
                        </div>
                        <p className={stampResult.success ? 'text-green-700' : 'text-red-700'}>
                          {stampResult.message}
                        </p>
                        {stampResult.success && stampResult.card && (
                          <div className="mt-3 p-3 bg-white rounded-lg border border-green-200">
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div>
                                <span className="text-gray-600">Tampons actuels:</span>
                                <span className="font-bold text-green-600 ml-2">{stampResult.card.stamps_count}/6</span>
                              </div>
                              <div>
                                <span className="text-gray-600">Total tampons:</span>
                                <span className="font-bold text-green-600 ml-2">{stampResult.card.total_stamps_earned}</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Stats */}
                {loyaltyStats && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                      <CardContent className="p-6 text-center">
                        <div className="text-4xl font-bold mb-2">{loyaltyStats.total_cards}</div>
                        <p className="text-blue-100">Cartes Créées</p>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                      <CardContent className="p-6 text-center">
                        <div className="text-4xl font-bold mb-2">{loyaltyStats.available_discounts}</div>
                        <p className="text-green-100">Réductions Disponibles</p>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
                      <CardContent className="p-6 text-center">
                        <div className="text-4xl font-bold mb-2">{loyaltyStats.total_stamps}</div>
                        <p className="text-amber-100">Tampons Donnés</p>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Recent Activity */}
                {loyaltyStats && loyaltyStats.recent_stamps && loyaltyStats.recent_stamps.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Activité Récente</CardTitle>
                      <CardDescription>Derniers tampons validés</CardDescription>
                    </CardHeader>
                    <CardContent className="p-6">
                      <div className="space-y-3">
                        {loyaltyStats.recent_stamps.map((stamp) => (
                          <div key={stamp.id} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                            <div>
                              <div className="font-semibold text-gray-900">{stamp.user_name}</div>
                              <div className="text-sm text-gray-500">Validé par: {stamp.admin_name}</div>
                            </div>
                            <div className="text-sm text-gray-500">
                              {new Date(stamp.created_at).toLocaleDateString('fr-FR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Instructions */}
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-bold text-blue-800 mb-4">Instructions</h3>
                    <div className="space-y-3 text-blue-700">
                      <div className="flex items-start gap-2">
                        <span className="font-bold">1.</span>
                        <span>Le client vous présente son QR code ou donne ses 4 chiffres</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="font-bold">2.</span>
                        <span>Scannez la carte avec la caméra ou saisissez le code à 4 chiffres</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="font-bold">3.</span>
                        <span>Cliquez sur "Valider le Tampon"</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="font-bold">4.</span>
                        <span>Le système indique si le client a débloqué sa réduction 10 €</span>
                      </div>
                    </div>
                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-yellow-800">
                        <strong>Important:</strong> Validez uniquement après service terminé
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;